/**
 * Pure validation for the child-profile form. No React, no i18n — callers map
 * the returned error *keys* through `t()` themselves.
 */

export type ChildErrorField = 'name' | 'dob' | 'range';

export interface ValidateChildInput {
  /** First name — the only required name. */
  name: string;
  /** `YYYY-MM-DD`, as a date input gives it; empty until the parent has picked a complete date. */
  dob: string;
  /** Age floor in months (e.g. 18) — pass `config.ageRange.minMonths`, or `minYears * 12` when the server hasn't sent it. */
  minMonths: number;
  maxYears: number;
  /** Injected so tests do not depend on the clock. */
  today?: Date;
}

export type ValidateChildResult =
  | { ok: true; ageMonths: number }
  | { ok: false; errors: Partial<Record<ChildErrorField, string>> };

const DOB_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Today as `YYYY-MM-DD` in the parent's own calendar day. */
export const isoDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** Whole months lived as of `today`, or `null` when the text is not a real calendar date. */
export const ageMonthsFromDob = (dob: string, today: Date = new Date()): number | null => {
  const m = DOB_PATTERN.exec(dob.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const real = new Date(y, mo - 1, d);
  if (real.getFullYear() !== y || real.getMonth() !== mo - 1 || real.getDate() !== d) return null;
  let months = (today.getFullYear() - y) * 12 + (today.getMonth() + 1 - mo);
  if (today.getDate() < d) months -= 1;
  return months;
};

export const validateChild = ({ name, dob, minMonths, maxYears, today = new Date() }: ValidateChildInput): ValidateChildResult => {
  const errors: Partial<Record<ChildErrorField, string>> = {};

  if (!name.trim()) {
    errors.name = 'child.nameError';
  }

  const ageMonths = ageMonthsFromDob(dob, today);
  if (ageMonths === null) {
    errors.dob = 'child.dobError';
  } else if (dob.trim() > isoDay(today)) {
    errors.dob = 'child.dobFuture';
  } else if (ageMonths < minMonths || ageMonths > maxYears * 12 + 11) {
    errors.range = 'child.rangeError';
  }

  if (Object.keys(errors).length === 0 && ageMonths !== null) {
    return { ok: true, ageMonths };
  }
  return { ok: false, errors };
};
